import React, { useCallback, useEffect, useState } from 'react';

// Lib
import config from 'lib/config';
import Cookies from 'js-cookie';
import { shallowEqual } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { filteredDataByKey } from 'lib/helpers';
import { getImageSources } from 'lib/libraries';
import { amplitudeInstance, events } from 'constans/cookie';
import { useAppDispatch, useAppSelector } from 'lib/hooks/useDispatch';

// Components
import Button from 'components/UiKit/Button';

// Action
import { downloadFile } from '../reducer';
import { toggleModal } from 'reducers/settings';

export const Download: React.FC = () => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const {
		lib,
		download,
		catNumber,
		highlight,
		libTargets,
		isAuthorized
	} = useAppSelector((store) => {
		return {
			isAuthorized: store.settings.user.authorized,
			highlight: store.librariesProduct?.highlightData,
			download: store.librariesProduct?.downloadLoading,
			lib: store.librariesProduct?.libDetailsData?.lib || {},
			catNumber: store.librariesProduct?.libDetailsData?.lib?.catNumber,
			libTargets: store.librariesProduct?.libDetailsData?.libTargets || []
		};
	}, shallowEqual);

	const [imageBanner, setImageMain] = useState<string>('');
	const [downloadPDF, setDownloadPDF] = useState<boolean>(false);
	const [configHighlights, setConfigHighlights] = useState<string[]>([]);

	const fetchImages = useCallback(async (
		urls: string[]
	) => {
		try {
			return await Promise.all(urls.map(async (url) => {
				const res = await fetch(url);
				const blob = await res.blob();
				return new Promise<string>((resolve) => {
					const fileReaderInstance = new FileReader();
					fileReaderInstance.readAsDataURL(blob);
					fileReaderInstance.onload = () => {
						resolve(fileReaderInstance.result as string);
					};
				});
			}));
		} catch (error) {
			console.log('Error in fetchImages', error);
			return [];
		}
	}, []);

	// Fetch banner image
	useEffect(() => {
		const {
			imgRight,
			imgCenter,
			srcRight,
			srcCenter
		} = getImageSources({
			lib,
			config,
			filterFn: filteredDataByKey
		});

		if (
			imgRight?.length ||
			imgCenter?.length
		) {
			const urlImageBannerLib = `${srcRight || srcCenter}`;
			fetchImages([urlImageBannerLib]).then((base64Images) => {
				if (base64Images?.length) {
					setImageMain(base64Images[0]);
				}
			});
		}
	}, [lib]);

	// Fetch highlight images and replace img field with base64
	useEffect(() => {
		const data = highlight?.slice(0, 4);
		if (data?.length) {
			const highlightUrls = data.map((item) => `${config.api.apiUrl}highlights/${item?.cdId}/image`);
			fetchImages(highlightUrls).then((base64Images) => {
				const updatedHighlights = data.map((item: any, index: number) => ({
					...item,
					img: base64Images[index]
				}));
				setConfigHighlights(updatedHighlights);
			});
		}
	}, [highlight]);

	const fetchHandler = (
		uniqId?: string,
		type?: string
	) => {
		const isCookie = Cookies.get('cookieName');

		if (type === 'sdf' && isCookie) {
			amplitudeInstance.logEvent(events.donwload_sdf_click, { catNumber: lib?.catNumber || '' });
		}

		if (type === 'xls' && isCookie) {
			amplitudeInstance.logEvent(events.donwload_xls_click, { catNumber: lib?.catNumber || '' });
		}

		dispatch(downloadFile({ uniqId, type }));
	};

	const handlerChangeGoToCompound = (data: any) => () => {
		if (!isAuthorized) {
			return dispatch(toggleModal(true));
		}

		if (!data.catNumber) return;

		const newData = {
			name: data.name,
			catNumber: data.catNumber,
			compoundsCount: data.compoundsCount
		};

		navigate(`/libraries-compounds/${data.catNumber}`, {
			state: newData
		});
	};

	const handlerPress = (
		id: string,
		type: string
	) => () => {
		if (!id) {
			return;
		}
		fetchHandler(id, type);
	};

	const handlerPressUnAuthorized = () => {
		dispatch(toggleModal(true));
	};

	const handlerDownloadPdf = async () => {
		if (!catNumber) return;

		const isCookie = Cookies.get('cookieName');
		setDownloadPDF(true);

		const  [pdfRenderer, fileSaver, pdfHelpers, pdfDoc] =
			await Promise.all([
				import('@react-pdf/renderer'),
				import('file-saver'),
				import("lib/libraries/pdfHelpers"),
				import('components/TempalePdf/LibrariesProduct/LibrariesProduct'),
			]);

		const { pdf } = pdfRenderer;
		const saveAs = fileSaver.saveAs || fileSaver.default?.saveAs;
		const { preparePdfDependencies, registerOpenSansFonts } = pdfHelpers;

		await preparePdfDependencies();
		await registerOpenSansFonts(pdfRenderer.Font);

		const LibrariesProductDocument = pdfDoc.default;

		const doc = (
			<LibrariesProductDocument
				description={lib}
				renderer={pdfRenderer}
				libTargets={libTargets}
				imageBanner={imageBanner}
				highlight={configHighlights}
			/>
		);

		const asPdf = pdf();
		asPdf.updateContainer(doc);
		const blob = await asPdf.toBlob();
		saveAs(blob, `${catNumber}.pdf`);
		setDownloadPDF(false);
		if (isCookie) {
			amplitudeInstance.logEvent(events.donwload_pdf_click, {
				catNumber: lib?.catNumber || '',
			});
		}
	};

	return (
		<div className="wrapper-lib-btn flex flex-ai-center">
			<div className="flex flex-ai-center wrapper-lib-btn__mb20-mobile" id="bioactive-libraries-download">
				<Button
					isSvg
					icon="xlsSvg"
					className="mr10"
					variable="download"
					loading={download?.xls}
					title="Download compound info (xls)"
					onPress={isAuthorized ? handlerPress(lib?.catNumber || '', 'xls') : handlerPressUnAuthorized}
				/>

				<Button
					isSvg
					icon="sdfSvg"
					className="mr10"
					variable="download"
					loading={download?.sdf}
					title="Download compound info (sdf)"
					onPress={isAuthorized ? handlerPress(lib?.catNumber || '', 'sdf') : handlerPressUnAuthorized}
				/>

				<Button
					isSvg
					icon="pdfSvg"
					className="mr10"
					variable="download"
					loading={downloadPDF}
					onPress={handlerDownloadPdf}
					title="Download library info (pdf)"
				/>
			</div>

			{lib?.cherryPicking && <Button
				isSvg
				icon="molSvg"
				variable="download"
				title="Go to the list of compounds"
				id="bioactive-libraries-btn-compound"
				className="wrapper-lib-btn__btn-mol-svg"
				onPress={handlerChangeGoToCompound(lib)}
				label="Cherry-picking of</br> molecules from the library"
			/>}
		</div>
	);
};

export default Download;